import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { simpleApiService } from '@/services/simpleApi'

// Rôles alignés avec le backend (apps/core/constants.py)
export type UserRole = 'admin' | 'medecin' | 'technicien' | 'assistant' | 'caissier'

// Note: 'assistant' = Secrétaire médicale / Assistante (même rôle)
// Note: 'technicien' = Manipulateur radio (même rôle)

interface User {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  display_name?: string
  photo?: string
  fonction?: string
  role: UserRole
  is_active: boolean
  telephone?: string
  specialite?: {
    id: number
    nom: string
    code: string
    acces_imagerie: boolean
  }
  service?: string
  est_chef_service?: boolean
  permissions_list?: string[]
  services?: { id: string; nom: string; code: string }[]
  permissions: {
    imagerie: {
      demandes: string[]
      examens: string[]
      comptes_rendus: string[]
    }
  }
  date_joined: string
  last_login?: string
}

interface AuthData {
  access: string
  refresh: string
  token_type?: string
  user: User
}

export const useSimpleAuthStore = defineStore('simpleAuth', () => {
  // État de l'authentification
  const isAuthenticated = ref(false)
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  
  // Getters
  const currentUser = computed(() => user.value)
  const userRole = computed(() => user.value?.role)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isMedecin = computed(() => user.value?.role === 'medecin')
  // Médecin ayant accès au module imagerie : role=medecin + specialite.acces_imagerie
  const isMedecinImagerie = computed(() =>
    user.value?.role === 'medecin' && user.value?.specialite?.acces_imagerie === true
  )
  const isTechnicien = computed(() => user.value?.role === 'technicien')  // = Manipulateur radio
  const isAssistant = computed(() => user.value?.role === 'assistant')   // = Secrétaire médicale
  const isCaissier = computed(() => user.value?.role === 'caissier')
  const isChefService = computed(() => user.value?.est_chef_service === true)
  // Alias pour compatibilité : secrétaire = assistant
  const isSecretaire = computed(() => user.value?.role === 'assistant')

  // Permissions propres aux groupes secrétaires (miroir du backend)
  const SECRETARY_PERMS = [
    'patients.can_view_patient', 'patients.can_create_patient',
    'imagerie.can_access_imagerie', 'imagerie.can_create_demande',
    'imagerie.can_view_examens', 'imagerie.can_manage_rdv',
    'imagerie.can_manage_transcriptions', 'imagerie.can_reassign_post_exam',
    'imagerie.can_view_cr', 'imagerie.can_manage_irm',
  ]

  // Deux types de secrétaires :
  // - Accueil  : gestion RDV uniquement (réception)
  // - Service  : toutes les permissions (tourne entre IRM, écho, salles…)
  const isSecretaireAccueil = computed(() =>
    user.value?.role === 'assistant' &&
    hasPermission('imagerie.can_manage_rdv') &&
    !hasPermission('imagerie.can_manage_transcriptions') &&
    !hasPermission('imagerie.can_manage_irm')
  )
  const isSecretaireService = computed(() =>
    user.value?.role === 'assistant' && !isSecretaireAccueil.value
  )

  /**
   * Vérifie si l'utilisateur possède une permission spécifique.
   * - Admin : bypass total
   * - Assistant sans aucune permission secrétaire dans sa liste → accès complet par défaut
   * - Assistant avec au moins une permission secrétaire → restreint aux permissions du groupe
   */
  function hasPermission(permission: string): boolean {
    if (!user.value) return false
    if (user.value.role === 'admin') return true
    if (user.value.role === 'assistant') {
      const list = user.value.permissions_list ?? []
      // Vérifie si un groupe secrétaire a été assigné (au moins une perm secrétaire présente)
      const hasSecretaryGroup = list.some(p => SECRETARY_PERMS.includes(p))
      if (!hasSecretaryGroup) return true  // Aucun groupe → accès complet
      return list.includes(permission)
    }
    return user.value.permissions_list?.includes(permission) ?? false
  }

  // Permissions spécialisées - Imagerie
  // Rôles imagerie : medecin+acces_imagerie, technicien, assistant (affectés auto au service IMG)
  const canAccessImagerie = computed(() => {
    const role = user.value?.role
    if (!role) return false

    // Admin, technicien et médecin imagerie ont accès complet
    if (role === 'admin' || role === 'technicien' || isMedecinImagerie.value) {
      return true
    }

    // Assistant : plein accès si pas de groupe, sinon selon permissions
    if (role === 'assistant') {
      return hasPermission('imagerie.can_access_imagerie')
    }

    return false
  })

  const isImagerieStaff = computed(() => {
    const role = user.value?.role
    // Médecin imagerie (medecin+acces_imagerie) et technicien
    if (isMedecinImagerie.value || role === 'technicien') return true

    // Assistant : vérifier le service d'affectation
    if (role !== 'assistant') return false

    const imagerieCodes = ['IMAGERIE', 'IMG', 'RADIOLOGIE', 'RAD']
    return user.value?.services?.some(service =>
      imagerieCodes.includes(service.code.toUpperCase())
    ) || false
  })

  const canManageImagerieRequests = computed(() =>
    user.value?.role === 'admin' ||
    isMedecinImagerie.value ||
    (user.value?.role === 'assistant' && isImagerieStaff.value)
  )

  const canManageImagerieSchedule = computed(() => {
    const role = user.value?.role
    return ['admin', 'technicien'].includes(role || '') ||
           isMedecinImagerie.value ||
           (role === 'assistant' && isImagerieStaff.value)
  })

  const canRealizeExamens = computed(() => {
    const role = user.value?.role
    return ['admin', 'technicien'].includes(role || '')  // Manipulateur radio
  })

  const canInterpretExamens = computed(() =>
    user.value?.role === 'admin' || isMedecinImagerie.value
  )

  const canValidateRapports = computed(() =>
    user.value?.role === 'admin' || isMedecinImagerie.value
  )

  // Permissions pour les transcriptions vocales
  const canAccessTranscriptions = computed(() =>
    user.value?.role === 'admin' ||
    isMedecinImagerie.value ||
    (user.value?.role === 'assistant' && isImagerieStaff.value)
  )

  const canEditTranscriptions = computed(() => {
    const role = user.value?.role
    return role === 'admin' || (role === 'assistant' && isImagerieStaff.value)
  })

  const canValidateTranscriptions = computed(() =>
    user.value?.role === 'admin' || isMedecinImagerie.value
  )

  const canListenToVoiceRecordings = computed(() => {
    const role = user.value?.role
    return user.value?.role === 'admin' || isMedecinImagerie.value ||
           (role === 'assistant' && isImagerieStaff.value)
  })
  
  // Permissions générales
  const canManagePatients = computed(() => {
    const role = user.value?.role
    return ['admin', 'assistant', 'caissier'].includes(role || '')
  })
  
  const canCreatePatients = computed(() => {
    const role = user.value?.role
    return ['admin', 'assistant', 'caissier'].includes(role || '')
  })
  
  const canManageRendezVous = computed(() => {
    const role = user.value?.role
    return ['admin', 'medecin', 'assistant'].includes(role || '')
  })
  
  const canManageUsers = computed(() => {
    return user.value?.role === 'admin'
  })
  
  const canAccessAdmin = computed(() => {
    return user.value?.role === 'admin'
  })
  
  const canManageBilling = computed(() => {
    const role = user.value?.role
    return ['admin', 'caissier'].includes(role || '')
  })
  
  // Actions
  const login = async (credentials: { username: string; password: string }) => {
    try {
      loading.value = true
      error.value = null
      
      console.log('🔐 Tentative de connexion avec:', credentials.username)
      
      const response = await simpleApiService.login(credentials)
      
      // Adapter les données du backend vers notre interface
      const authData: AuthData = {
        access: response.access,
        refresh: response.refresh,
        token_type: response.token_type || 'Bearer',
        user: {
          ...response.user,
          full_name: `${response.user.first_name} ${response.user.last_name}`,
          services: [],
          permissions: {
            imagerie: {
              demandes: [],
              examens: [],
              comptes_rendus: []
            }
          },
          date_joined: new Date().toISOString()
        }
      }
      
      // Tokens reçus en cookies httpOnly — seule la donnée utilisateur est stockée
      isAuthenticated.value = true
      user.value = authData.user
      localStorage.setItem('user', JSON.stringify(authData.user))

      return authData
    } catch (err: any) {
      error.value = err.message || 'Erreur de connexion'
      console.error('❌ Erreur de connexion:', err)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  const logout = async () => {
    try {
      loading.value = true
      
      await simpleApiService.logout()
      
      // Réinitialiser l'état
      isAuthenticated.value = false
      user.value = null
      accessToken.value = null
      refreshToken.value = null
      error.value = null
      
    } catch (err: any) {
      if (import.meta.env.DEV) console.error('[Auth] Erreur lors de la déconnexion:', err)
    } finally {
      loading.value = false
    }
  }
  
  const restoreAuth = () => {
    try {
      const userData = localStorage.getItem('user')
      if (userData) {
        user.value = JSON.parse(userData)
        isAuthenticated.value = true
        // Les tokens sont dans des cookies httpOnly — validés côté serveur à la première requête
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Auth] Erreur restauration de session:', err)
      logout()
    }
  }
  
  const clearError = () => {
    error.value = null
  }

  // Méthode pour rafraîchir les données utilisateur
  const refreshUser = async () => {
    if (!isAuthenticated.value) return

    try {
      loading.value = true
      const userData = await simpleApiService.get('/auth/profile/')
      user.value = userData
      localStorage.setItem('user', JSON.stringify(userData))
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Auth] Erreur rafraîchissement utilisateur:', err)
    } finally {
      loading.value = false
    }
  }

  // Initialiser la restauration de session
  restoreAuth()
  
  return {
    // État
    isAuthenticated,
    user,
    accessToken,
    refreshToken,
    loading,
    error,
    
    // Getters de rôles
    currentUser,
    userRole,
    isAdmin,
    isMedecin,
    isMedecinImagerie,
    isTechnicien,
    isAssistant,
    isCaissier,
    isChefService,
    isSecretaire,  // Alias de isAssistant
    isSecretaireAccueil,
    isSecretaireService,

    // Permissions imagerie
    canAccessImagerie,
    isImagerieStaff,
    canManageImagerieRequests,
    canManageImagerieSchedule,
    canRealizeExamens,
    canInterpretExamens,
    canValidateRapports,
    
    // Permissions transcriptions
    canAccessTranscriptions,
    canEditTranscriptions,
    canValidateTranscriptions,
    canListenToVoiceRecordings,
    
    // Permissions générales
    canManagePatients,
    canCreatePatients,
    canManageRendezVous,
    canManageUsers,
    canAccessAdmin,
    canManageBilling,
    
    // Actions
    login,
    logout,
    restoreAuth,
    clearError,
    refreshUser,

    // Contrôle d'accès granulaire (permission Django)
    hasPermission,
  }
})